import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LearningDevelopmentService } from '@app/shared/services/learning-development.service';
import { LearningDevelopmentData } from '@app/shared/interfaces/learning-development.model';
import { AuthService } from '@app/core/auth.service';
import { ErrorMessages } from '@app/shared/constants/constants';
import { GlobalErrorService } from '@app/shared/services/global-error.service';
import { LEARNING_DEVELOPMENT } from '@app/shared/constants/learning-development.constants';
import { getCurrentQuarter } from '@app/shared/utils/utils';
import { 
  learningDevelopmentColumnDefs, 
  learningDevelopmentPaginationParams,
  transformToDisplayData
} from './learning-development.helper';
import { RoutesEnum } from '@app/shared/constants/global.enum';

interface LearningDevelopmentDisplayData {
  name: string;
  year: number;
  tenHrsTraining: string;
  upskilledToFullstack: string;
  aiMlAndGenAiSkills: string;
  numberOfCertificationRequired: number;
  quarter: string;
}

@Component({
  selector: 'app-learning-development',
  templateUrl: './learning-development.component.html',
  styleUrls: ['./learning-development.component.scss'],
})
export class LearningDevelopmentComponent implements OnInit {

  @Input() persona: string = '';
  @Input() vendorId: string | null = null;

  contractors: LearningDevelopmentData[] = [];
  displayContractors: LearningDevelopmentDisplayData[] = [];
  filteredContractors: LearningDevelopmentDisplayData[] = [];
  searchText = '';
  page = 1;
  pageSize = LEARNING_DEVELOPMENT.PAGINATION.DEFAULT_PAGE_SIZE;
  isLoading = false;
  errorMessage: string | null = null;
  imageSrc: string = 'assets/img/no-data-found.png';
  noDataMessage: string = ErrorMessages.NoDataAvailable;
  title: string = LEARNING_DEVELOPMENT.TEXT.TITLE;
  searchLabel: string = LEARNING_DEVELOPMENT.TEXT.SEARCH;
  tableMaxHeight: number = LEARNING_DEVELOPMENT.PAGINATION.MAX_HEIGHT;
  noDataAltText: string = LEARNING_DEVELOPMENT.TEXT.NO_DATA_ALT;
  RoutesEnum = RoutesEnum;
  columnDefs = learningDevelopmentColumnDefs;
  paginationParams = learningDevelopmentPaginationParams;

  constructor(
    private ldService: LearningDevelopmentService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private errorHandler: GlobalErrorService
  ) {}

  ngOnInit() {
    if (!this.route) {
      return;
    }
    if (this.vendorId) {
      this.loadLearningData(this.vendorId);
    }
  }

  private loadLearningData(vendorId: string) {
    this.isLoading = true;
    this.errorMessage = null;

    this.authService.getEmployeeDetails().then((employeeData: any) => {
      if (employeeData?.employeeId) {
        const quarter = getCurrentQuarter();
        const year = new Date().getFullYear();
        this.ldService
          .getLearningDevelopmentData(
            vendorId,
            this.persona == RoutesEnum.OpsVendorManagement ? '' : employeeData.employeeId,
            quarter,
            year,
          )
          .subscribe({
            next: (response) => {
              this.contractors = response.data || [];
              this.displayContractors = transformToDisplayData(this.contractors);
              this.filterData();
              this.isLoading = false;
            },
            error: (error) => {
              this.errorMessage = LEARNING_DEVELOPMENT.TEXT.LOADING_ERROR;
              this.isLoading = false;
              this.errorHandler.logComponentError(error);
            },
          });
      }
    });
  }

  filterData() {
    if (!this.searchText.trim()) {
      this.filteredContractors = [...this.displayContractors];
    } else {
      const searchLower = this.searchText.toLowerCase();
      this.filteredContractors = this.displayContractors.filter((c) =>
        c.name.toLowerCase().includes(searchLower),
      );
    }
  }

  filterValue = '';

  /**
   * Updates search filter value and triggers filtering
   */
  onSearchChange(event: any) {
    this.updateSearchText(event.target.value);
  }

  /**
   * Updates search text and filters data
   */
  private updateSearchText(text: string) {
    this.filterValue = text;
    this.searchText = text;
    this.filterData();
  }

  /**
   * Clears the search input and resets filtering
   */
  clearInput() {
    this.updateSearchText('');
  }

  get paginatedContractors() {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredContractors.slice(start, start + this.pageSize);
  }

  onPageChange(newPage: number) {
    this.page = newPage;
  }
}
